import { useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { useJudgingProgress, useMyJudgingCompetitions, useMyScores } from '@/hooks/useJudging';
import { useCategories } from '@/hooks/useCategories';
import { useSubmissions } from '@/hooks/useSubmissions';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { InfoHint } from '@/components/ui/Tooltip';
import { SubmissionThumbnail } from '@/components/submissions/SubmissionThumbnail';
import { CompetitionStatus, type Submission } from '@/api/types';

export function JudgeScoringPage() {
  const competitions = useMyJudgingCompetitions();
  const location = useLocation();
  // Selection lives in the URL so that "back to the queue" from an entry
  // returns to the same competition and category, and a reload keeps it.
  const [searchParams, setSearchParams] = useSearchParams();
  const competitionId = searchParams.get('competition') ?? '';
  const categoryId = searchParams.get('category') ?? '';
  function setCompetitionId(id: string) {
    setSearchParams(id ? { competition: id } : {}, { replace: true });
  }
  function setCategoryId(id: string) {
    setSearchParams(id ? { competition: competitionId, category: id } : { competition: competitionId }, { replace: true });
  }
  // A judge with a single competition has nothing to choose: open it straight away.
  const onlyCompetition = competitions.data?.items.length === 1 ? competitions.data.items[0] : undefined;
  useEffect(() => {
    if (!competitionId && onlyCompetition) setSearchParams({ competition: onlyCompetition.id }, { replace: true });
  }, [competitionId, onlyCompetition, setSearchParams]);
  const categories = useCategories(competitionId || undefined);
  // The list shows every category's entries until one is picked (then just
  // that one). Scoring itself stays per category either way: an entry opens in
  // a queue made of its own category only (see SubmissionDetailsPage), so a
  // judge's sense of scale is never mixed across categories.
  const submissions = useSubmissions(
    categoryId ? { categoryId } : { competitionId },
    { enabled: Boolean(competitionId) },
  );
  const progress = useJudgingProgress(competitionId || undefined);
  const myScores = useMyScores(competitionId || undefined);
  const scoreBySubmission = new Map((myScores.data?.items ?? []).map((sc) => [sc.submissionId, sc.value]));
  const selectedCompetition = competitions.data?.items.find((c) => c.id === competitionId);
  const notJudgingYet = selectedCompetition !== undefined && selectedCompetition.status < CompetitionStatus.Judging;

  // Entries grouped in the competition's category order; categories that have
  // no entries are left out, entries of an unknown category go last.
  const categoryList = categories.data?.items ?? [];
  const items = submissions.data?.items ?? [];
  const groups: { id: string; name: string; items: Submission[] }[] = [];
  for (const category of categoryList) {
    const inCategory = items.filter((item) => item.categoryId === category.id);
    if (inCategory.length > 0) groups.push({ id: category.id, name: category.name, items: inCategory });
  }
  const known = new Set(categoryList.map((c) => c.id));
  const other = items.filter((item) => !known.has(item.categoryId));
  if (other.length > 0) groups.push({ id: 'other', name: 'Ostalo', items: other });

  const ordered = groups.flatMap((g) => g.items);
  const firstUnscored = ordered.find((item) => !scoreBySubmission.has(item.id));
  const scoredCount = ordered.filter((item) => scoreBySubmission.has(item.id)).length;
  const unscoredCount = ordered.length - scoredCount;
  const showGroupHeaders = !categoryId && groups.length > 0;

  // Entries are opened with the current list URL in the router state, so the
  // "back" link on the entry page returns to exactly this selection.
  const linkState = { backTo: `${location.pathname}${location.search}` };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-fg">Čakalna vrsta za ocenjevanje</h1>
        <p className="text-sm text-fg-muted">
          Izberite eno od svojih dodeljenih tekmovanj za pregled in ocenjevanje njegovih prispevkov. Kategorijo lahko
          izberete za ožji pregled; ocenjevanje poteka vedno znotraj kategorije prispevka.
        </p>
      </div>

      {competitions.isLoading && <PageSpinner />}
      {competitions.isError && (
        <ErrorState message={getApiErrorMessage(competitions.error)} onRetry={() => competitions.refetch()} />
      )}
      {competitions.isSuccess && competitions.data.items.length === 0 && (
        <EmptyState title="Še niste dodeljeni kot sodnik na nobenem tekmovanju" />
      )}

      {competitions.isSuccess && competitions.data.items.length > 0 && (
        <div className="flex max-w-2xl flex-wrap gap-3">
          <div className="max-w-sm flex-1">
            <Select label="Tekmovanje" value={competitionId} onChange={(e) => setCompetitionId(e.target.value)}>
              <option value="">Izberite tekmovanje&hellip;</option>
              {competitions.data.items.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          {competitionId && (
            <div className="max-w-sm flex-1">
              <Select
                label="Kategorija (neobvezno)"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                disabled={categories.isLoading}
              >
                <option value="">Vse kategorije</option>
                {categoryList.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>
      )}

      {competitions.isSuccess && competitions.data.items.length > 0 && !competitionId && (
        <EmptyState title="Izberite tekmovanje za ogled njegovih prispevkov" />
      )}

      {competitionId && progress.isSuccess && (
        <Card>
          <CardBody>
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <h2 className="text-sm font-semibold text-fg">Napredek ocenjevanja</h2>
                <InfoHint text="Šteje samo sprejete prispevke - ostali prispevki niso na voljo za ocenjevanje." />
              </div>
              {progress.data.remaining === 0 && progress.data.totalToJudge > 0 ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
                  <CheckCircle2 className="h-4 w-4" /> Vse ocenjeno
                </span>
              ) : (
                <span className="text-xs font-medium text-fg-muted">Še {progress.data.remaining} preostalo</span>
              )}
            </div>
            <ProgressBar
              value={progress.data.judgedByMe}
              max={progress.data.totalToJudge}
            />
            <p className="mt-1.5 text-xs text-fg-muted">
              Ocenili ste {progress.data.judgedByMe} od {progress.data.totalToJudge} sprejetih prispevkov.
            </p>
          </CardBody>
        </Card>
      )}

      {competitionId && submissions.isLoading && <PageSpinner />}
      {competitionId && submissions.isError && (
        <ErrorState message={getApiErrorMessage(submissions.error)} onRetry={() => submissions.refetch()} />
      )}
      {competitionId && submissions.isSuccess && ordered.length === 0 && (
        notJudgingYet ? (
          <EmptyState
            title="Ocenjevanje se še ni začelo"
            description="Prispevki bodo na voljo, ko organizator začne fazo ocenjevanja."
          />
        ) : (
          <EmptyState title={categoryId ? 'Ni prispevkov za ocenjevanje v tej kategoriji' : 'Ni prispevkov za ocenjevanje'} />
        )
      )}
      {competitionId && submissions.isSuccess && ordered.length > 0 && (
        <div className="flex flex-col gap-2">
          {firstUnscored ? (
            <div className="flex items-center justify-between gap-3 rounded-control border border-line bg-surface-1 px-4 py-3">
              <p className="text-sm text-fg-muted">
                Neocenjenih: {unscoredCount} od {ordered.length}
              </p>
              <Link to={`/submissions/${firstUnscored.id}`} state={linkState}>
                <Button size="sm">{scoredCount > 0 ? 'Nadaljuj z ocenjevanjem' : 'Začni ocenjevati'}</Button>
              </Link>
            </div>
          ) : (
            <p className="inline-flex items-center gap-1.5 text-sm font-medium text-success">
              <CheckCircle2 className="h-4 w-4" /> {categoryId ? 'Vse slike v kategoriji so ocenjene' : 'Vse slike so ocenjene'}
            </p>
          )}

          {groups.map((group) => {
            const groupScored = group.items.filter((item) => scoreBySubmission.has(item.id)).length;
            return (
              <section key={group.id} className="flex flex-col gap-2">
                {showGroupHeaders && (
                  <div className="mt-2 flex items-baseline justify-between gap-2">
                    <h2 className="text-sm font-semibold text-fg">{group.name}</h2>
                    <span className="text-xs text-fg-muted">
                      Ocenjeno {groupScored} od {group.items.length}
                    </span>
                  </div>
                )}
                {group.items.map((s) => (
                  <Card key={s.id}>
                    <CardBody className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-photo bg-surface-3">
                          <SubmissionThumbnail submissionId={s.id} alt={s.title} />
                        </div>
                        <div>
                          <p className="font-medium text-fg">{s.title}</p>
                          {scoreBySubmission.has(s.id) ? (
                            <Badge tone="Accepted">Ocenjeno: {scoreBySubmission.get(s.id)}</Badge>
                          ) : (
                            <Badge tone="Draft">Neocenjeno</Badge>
                          )}
                        </div>
                      </div>
                      <Link to={`/submissions/${s.id}`} state={linkState}>
                        <Button size="sm" variant={scoreBySubmission.has(s.id) ? 'secondary' : undefined}>
                          {scoreBySubmission.has(s.id) ? 'Uredi oceno' : 'Oceni'}
                        </Button>
                      </Link>
                    </CardBody>
                  </Card>
                ))}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
